import { useEffect, useMemo, useState } from "react";
import {
  Avatar,
  Box,
  Card,
  Chip,
  CircularProgress,
  Dialog,
  DialogContent,
  DialogTitle,
  IconButton,
  InputAdornment,
  Pagination,
  Paper,
  Stack,
  TextField,
  Typography,
  alpha,
  useMediaQuery,
  useTheme,
} from "@mui/material";
import AccessTimeIcon from "@mui/icons-material/AccessTime";
import AddRoundedIcon from "@mui/icons-material/AddRounded";
import AutoAwesomeRoundedIcon from "@mui/icons-material/AutoAwesomeRounded";
import ChatRoundedIcon from "@mui/icons-material/ChatRounded";
import CloseRoundedIcon from "@mui/icons-material/CloseRounded";
import ImageOutlinedIcon from "@mui/icons-material/ImageOutlined";
import SearchIcon from "@mui/icons-material/Search";
import SendRoundedIcon from "@mui/icons-material/SendRounded";
import parse from "html-react-parser";
import MDButton from "components/MDButton";
import MDBox from "components/MDBox";
import DashboardLayout from "examples/LayoutContainers/DashboardLayout";
import DashboardNavbar from "examples/Navbars/DashboardNavbar";
import {
  askAIChatCall,
  askTutorChatCall,
  getAIMessagesByThreadCall,
  getAllAIThreadsCall,
} from "../../redux/api/ai.api";
import { getCopilotCreditsCall } from "../../redux/api/copilot.api";
import InsufficientCreditsModal from "../microlabCopilot/components/InsufficientCreditsModal";

const TUTOR_MARKER = "<!--microlab-tutor:";

function tutorHtml(content) {
  const raw = String(content || "");
  const index = raw.lastIndexOf(TUTOR_MARKER);
  if (index === -1) return raw;
  return raw.slice(0, index).trim();
}

function AIHistory() {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down("sm"));

  const [threads, setThreads] = useState([]);
  const [loading, setLoading] = useState(false);
  const [search, setSearch] = useState("");
  const [selectedThread, setSelectedThread] = useState(null);
  const [messages, setMessages] = useState([]);
  const [chatOpen, setChatOpen] = useState(false);
  const [chatLoading, setChatLoading] = useState(false);
  const [input, setInput] = useState("");
  const [sending, setSending] = useState(false);
  const [page, setPage] = useState(1);
  const [askingNew, setAskingNew] = useState(false);
  const [credits, setCredits] = useState(null);
  const [insufficient, setInsufficient] = useState(null);
  const pageSize = 10;

  const renderAiHtml = (html) => (
    <Box
      sx={{
        fontSize: 15,
        lineHeight: 1.6,
        "& p": { margin: 0, mb: 1 },
        "& p:last-of-type": { mb: 0 },
        "& ul, & ol": { margin: "6px 0 0", pl: 2.5 },
        "& li": { marginBottom: 0.5 },
        "& li:last-of-type": { marginBottom: 0 },
        "& strong, & b": { fontWeight: 700 },
      }}
    >
      {parse(html || "")}
    </Box>
  );

  const fetchThreads = async () => {
    setLoading(true);
    try {
      const locale = (localStorage.getItem("i18nextLng") || "en").split("-")[0];
      const res = await getAllAIThreadsCall(locale);
      const grouped = res?.threads || {};
      const flat = Object.entries(grouped).flatMap(([groupLabel, items]) =>
        (Array.isArray(items) ? items : []).map((item) => ({
          ...item,
          groupLabel,
        }))
      );
      flat.sort((a, b) => new Date(b.created_at) - new Date(a.created_at));
      setThreads(flat);
    } finally {
      setLoading(false);
    }
  };

  const refreshCredits = async () => {
    const next = await getCopilotCreditsCall();
    setCredits(next);
    return next;
  };

  useEffect(() => {
    fetchThreads();
    refreshCredits();
  }, []);

  const filteredThreads = useMemo(() => {
    const q = search.trim().toLowerCase();
    if (!q) return threads;
    return threads.filter((t) => (t.first_user_message || "").toLowerCase().includes(q));
  }, [threads, search]);

  const pageCount = Math.max(1, Math.ceil(filteredThreads.length / pageSize));
  const pagedThreads = useMemo(() => {
    const start = (page - 1) * pageSize;
    return filteredThreads.slice(start, start + pageSize);
  }, [filteredThreads, page]);

  useEffect(() => {
    setPage(1);
  }, [search]);

  useEffect(() => {
    if (page > pageCount) {
      setPage(pageCount);
    }
  }, [page, pageCount]);

  const startNewQuestion = () => {
    setAskingNew(true);
    setSelectedThread(null);
    setMessages([]);
    setInput("");
    setChatLoading(false);
    setChatOpen(true);
  };

  const openThread = async (thread) => {
    setAskingNew(false);
    setSelectedThread(thread);
    setChatOpen(true);
    setChatLoading(true);
    try {
      const res = await getAIMessagesByThreadCall(thread.thread_id);
      const mapped = Array.isArray(res)
        ? [...res]
            .reverse()
            .map((m) => ({
              id: m._id,
              sender: m?.user?._id === "ai" ? "ai" : "user",
              text: m?.text || "",
              image: m?.image || m?.file?.url || "",
              createdAt: m?.createdAt,
            }))
        : [];
      setMessages(mapped);
    } finally {
      setChatLoading(false);
    }
  };

  const sendTutorQuestion = async (question) => {
    const isFirstQuestion = !selectedThread?.thread_id;
    const askCost = Number(credits?.costs?.ai_question_base ?? 1);
    if (
      isFirstQuestion &&
      credits &&
      Number(credits.creditsLimit) > 0 &&
      Number(credits.creditsRemaining) < askCost
    ) {
      setInsufficient({
        required: askCost,
        remaining: Number(credits.creditsRemaining) || 0,
      });
      return;
    }

    setSending(true);
    setInput("");
    const optimisticId = `tmp-user-${Date.now()}`;
    setMessages((prev) => [
      ...prev,
      { id: optimisticId, sender: "user", text: question, image: "" },
    ]);
    try {
      const locale = (localStorage.getItem("i18nextLng") || "en").split("-")[0];
      const formData = new FormData();
      formData.append("body", question);
      formData.append("type", "ai");
      formData.append("entity_type", "aiQuestion");
      formData.append("language", locale || "en");
      if (selectedThread?.thread_id) {
        formData.append("thread_id", selectedThread.thread_id);
      }
      const res = await askTutorChatCall(formData);
      if (res?.status === 402 || res?.code === "INSUFFICIENT_CREDITS") {
        setMessages((prev) => prev.filter((item) => item.id !== optimisticId));
        setInput(question);
        setInsufficient({
          required: res?.credits?.required ?? askCost,
          remaining: Number(res?.credits?.creditsRemaining) || 0,
        });
        return;
      }
      if (!res || (res.status && res.status >= 400) || !res.thread_id) {
        setMessages((prev) => [
          ...prev,
          {
            id: `tmp-ai-${Date.now()}`,
            sender: "ai",
            text: res?.message || "The question could not be sent. Try again.",
            image: "",
          },
        ]);
        return;
      }
      const aiText = tutorHtml(res?.tutor?.answer || res?.messages?.[1]?.content || "");
      if (aiText) {
        setMessages((prev) => [
          ...prev,
          { id: `tmp-ai-${Date.now()}`, sender: "ai", text: aiText, image: "" },
        ]);
      }
      setSelectedThread((current) => ({
        ...(current || {}),
        thread_id: res.thread_id,
        first_user_message: current?.first_user_message || question,
      }));
      if (isFirstQuestion) {
        fetchThreads();
        refreshCredits();
      }
    } finally {
      setSending(false);
    }
  };

  const sendMessage = async () => {
    const question = input.trim();
    if (!question || sending) return;
    if (askingNew) {
      await sendTutorQuestion(question);
      return;
    }
    if (!selectedThread?.thread_id) return;
    setSending(true);
    setInput("");
    setMessages((prev) => [
      ...prev,
      { id: `tmp-user-${Date.now()}`, sender: "user", text: question, image: "" },
    ]);
    try {
      const formData = new FormData();
      formData.append("body", question);
      formData.append("type", "ai");
      formData.append("entity_type", "aiQuestion");
      formData.append("thread_id", selectedThread.thread_id);
      formData.append("title", "histology");
      const res = await askAIChatCall(formData);
      const aiMessage = res?.messages?.[1]?.content || "";
      if (aiMessage) {
        setMessages((prev) => [
          ...prev,
          { id: `tmp-ai-${Date.now()}`, sender: "ai", text: aiMessage, image: "" },
        ]);
      }
    } finally {
      setSending(false);
    }
  };

  const closeChat = () => {
    setChatOpen(false);
    setAskingNew(false);
  };
  const freshQuestion = askingNew && !selectedThread?.thread_id;
  const askCost = Number(credits?.costs?.ai_question_base ?? 1);

  return (
    <DashboardLayout>
      <DashboardNavbar />
      <MDBox pt={2} pb={3}>
        <Card
          sx={{
            borderRadius: 4,
            overflow: "hidden",
            border: "1px solid rgba(148,163,184,0.22)",
            boxShadow: "0 24px 60px rgba(15, 23, 42, 0.08)",
          }}
        >
          <Box
            sx={{
              px: { xs: 2, sm: 3 },
              py: 2.5,
              color: "#0f172a",
              background: "linear-gradient(180deg, #f8fafc 0%, #eef2ff 100%)",
              borderBottom: "1px solid rgba(148,163,184,0.22)",
            }}
          >
            <Stack
              direction={isMobile ? "column" : "row"}
              justifyContent="space-between"
              alignItems={isMobile ? "stretch" : "center"}
              spacing={2}
            >
              <Stack direction="row" spacing={1.6} alignItems="center">
                <IconButton
                  aria-label="Ask a question"
                  onClick={startNewQuestion}
                  sx={{
                    width: 52,
                    height: 52,
                    color: "#fff",
                    background: "linear-gradient(135deg, #6366f1, #2563eb)",
                    boxShadow: "0 10px 24px rgba(37, 99, 235, 0.45)",
                    "&:hover": { background: "linear-gradient(135deg, #4f46e5, #1d4ed8)" },
                  }}
                >
                  <AddRoundedIcon />
                </IconButton>
                <Box>
                  <Typography sx={{ fontSize: 11, letterSpacing: 1.4, fontWeight: 800, color: "#6366f1" }}>
                    MICROLAB AI
                  </Typography>
                  <Typography variant="h5" fontWeight={800} sx={{ lineHeight: 1.15, color: "#0f172a" }}>
                    AI History
                  </Typography>
                  <Typography variant="body2" sx={{ color: "#64748b", mt: 0.3 }}>
                    Pick up a conversation, or start a new question.
                  </Typography>
                </Box>
              </Stack>
              <TextField
                size="small"
                value={search}
                onChange={(e) => setSearch(e.target.value)}
                placeholder="Search conversations"
                sx={{
                  width: isMobile ? "100%" : 320,
                  "& .MuiOutlinedInput-root": {
                    borderRadius: 999,
                    color: "#0f172a",
                    backgroundColor: "#ffffff",
                    "& fieldset": { borderColor: "rgba(148,163,184,0.45)" },
                    "&:hover fieldset": { borderColor: "#94a3b8" },
                  },
                }}
                InputProps={{
                  startAdornment: (
                    <InputAdornment position="start">
                      <SearchIcon fontSize="small" sx={{ color: "#94a3b8" }} />
                    </InputAdornment>
                  ),
                }}
              />
            </Stack>
          </Box>

          <Box sx={{ p: { xs: 1.5, sm: 2.5 }, backgroundColor: "#f8fafc" }}>
          {loading ? (
            <Box sx={{ py: 8, textAlign: "center" }}>
              <CircularProgress size={28} />
            </Box>
          ) : filteredThreads.length === 0 ? (
            <Paper
              elevation={0}
              sx={{
                p: 5,
                textAlign: "center",
                borderRadius: 3,
                border: "1px dashed rgba(99,102,241,0.35)",
                backgroundColor: "#fff",
              }}
            >
              <AutoAwesomeRoundedIcon sx={{ color: "#6366f1", fontSize: 32, mb: 1 }} />
              <Typography fontWeight={700}>No conversations yet</Typography>
              <Typography variant="body2" color="text.secondary" sx={{ mt: 0.5, mb: 2 }}>
                Ask a new question and it will show up here.
              </Typography>
              <MDButton color="info" variant="gradient" onClick={startNewQuestion} startIcon={<AddRoundedIcon />}>
                Ask a question
              </MDButton>
            </Paper>
          ) : (
            <Stack spacing={1.25}>
              {pagedThreads.map((thread) => {
                const preview = thread.first_user_message || "No preview";
                const image = thread.picture_url || thread.uploaded_file?.url || "";
                return (
                  <Paper
                    key={thread.thread_id}
                    elevation={0}
                    onClick={() => openThread(thread)}
                    sx={{
                      p: 1.5,
                      borderRadius: 3,
                      cursor: "pointer",
                      backgroundColor: "#fff",
                      border: "1px solid rgba(148,163,184,0.28)",
                      transition: "transform .18s ease, box-shadow .18s ease, border-color .18s ease",
                      "&:hover": {
                        transform: "translateY(-2px)",
                        boxShadow: "0 16px 36px rgba(15, 23, 42, 0.08)",
                        borderColor: "#818cf8",
                      },
                    }}
                  >
                    <Stack direction="row" spacing={1.5} alignItems="center">
                      {image ? (
                        <Box
                          component="img"
                          src={image}
                          alt=""
                          sx={{
                            width: 64,
                            height: 64,
                            borderRadius: 2.5,
                            objectFit: "cover",
                            flexShrink: 0,
                          }}
                        />
                      ) : (
                        <Avatar
                          variant="rounded"
                          sx={{
                            width: 64,
                            height: 64,
                            borderRadius: 2.5,
                            color: "#fff",
                            background: "linear-gradient(135deg, #6366f1, #2563eb)",
                          }}
                        >
                          <ChatRoundedIcon />
                        </Avatar>
                      )}

                      <Box sx={{ flex: 1, minWidth: 0 }}>
                        <Stack direction="row" alignItems="center" justifyContent="space-between" spacing={1}>
                          <Chip
                            size="small"
                            label={thread.groupLabel || "Recent"}
                            sx={{
                              height: 22,
                              fontWeight: 700,
                              color: "#4338ca",
                              backgroundColor: alpha("#6366f1", 0.12),
                            }}
                          />
                          <Stack direction="row" spacing={0.5} alignItems="center">
                            <AccessTimeIcon sx={{ fontSize: 14, color: "text.secondary" }} />
                            <Typography variant="caption" color="text.secondary">
                              {new Date(thread.created_at).toLocaleString()}
                            </Typography>
                          </Stack>
                        </Stack>
                        <Typography
                          sx={{
                            mt: 0.7,
                            fontWeight: 700,
                            overflow: "hidden",
                            textOverflow: "ellipsis",
                            display: "-webkit-box",
                            WebkitLineClamp: 2,
                            WebkitBoxOrient: "vertical",
                          }}
                        >
                          {preview}
                        </Typography>
                        {image ? (
                          <Stack direction="row" spacing={0.4} alignItems="center" mt={0.5}>
                            <ImageOutlinedIcon sx={{ fontSize: 14, color: "text.secondary" }} />
                            <Typography variant="caption" color="text.secondary">
                              Image attached
                            </Typography>
                          </Stack>
                        ) : null}
                      </Box>
                    </Stack>
                  </Paper>
                );
              })}
              <Stack
                direction={isMobile ? "column" : "row"}
                alignItems="center"
                justifyContent="space-between"
                spacing={1}
                pt={0.5}
              >
                <Typography variant="caption" color="text.secondary">
                  Showing {filteredThreads.length === 0 ? 0 : (page - 1) * pageSize + 1}
                  -{Math.min(page * pageSize, filteredThreads.length)} of {filteredThreads.length}
                </Typography>
                <Pagination
                  page={page}
                  count={pageCount}
                  color="primary"
                  shape="rounded"
                  onChange={(_, value) => setPage(value)}
                  size={isMobile ? "small" : "medium"}
                />
              </Stack>
            </Stack>
          )}
          </Box>
        </Card>
      </MDBox>

      <Dialog
        open={chatOpen}
        onClose={closeChat}
        fullScreen={isMobile}
        fullWidth
        maxWidth="md"
        PaperProps={{
          sx: {
            borderRadius: isMobile ? 0 : 4,
            overflow: "hidden",
            backgroundColor: "#0b1220",
            color: "#fff",
            boxShadow: "0 30px 80px rgba(2, 6, 23, 0.45)",
          },
        }}
      >
        <DialogTitle
          sx={{
            pr: 6,
            py: 2,
            background:
              "radial-gradient(500px 80px at 10% 0%, rgba(99,102,241,0.45), transparent 60%), linear-gradient(135deg, #111827, #1e1b4b)",
          }}
        >
          <Stack direction="row" spacing={1.2} alignItems="center">
            <Avatar sx={{ width: 36, height: 36, background: "linear-gradient(135deg, #6366f1, #2563eb)" }}>
              <AutoAwesomeRoundedIcon fontSize="small" />
            </Avatar>
            <Box>
              <Typography fontWeight={800} sx={{ lineHeight: 1.1 }}>
                {freshQuestion ? "Ask a question" : "Continue chat"}
              </Typography>
              <Typography variant="caption" sx={{ color: "rgba(255,255,255,0.62)" }}>
                {freshQuestion ? `A new question uses ${askCost} credit.` : "Follow-ups in this chat stay in the same thread."}
              </Typography>
            </Box>
          </Stack>
          <IconButton
            onClick={closeChat}
            sx={{ position: "absolute", right: 10, top: 10, color: "#fff" }}
          >
            <CloseRoundedIcon />
          </IconButton>
        </DialogTitle>
        <DialogContent sx={{ px: 2, pb: 2, backgroundColor: "#0b1220" }}>
          {chatLoading ? (
            <Box sx={{ py: 8, textAlign: "center" }}>
              <CircularProgress size={24} sx={{ color: "#a5b4fc" }} />
            </Box>
          ) : (
            <>
              <Box
                sx={{
                  p: 1.5,
                  minHeight: 220,
                  maxHeight: isMobile ? "62vh" : 460,
                  overflowY: "auto",
                  borderRadius: 3,
                  backgroundColor: "rgba(255,255,255,0.03)",
                  border: "1px solid rgba(255,255,255,0.06)",
                }}
              >
                {messages.length === 0 ? (
                  <Stack alignItems="center" justifyContent="center" sx={{ minHeight: 180, textAlign: "center", px: 2 }}>
                    <AutoAwesomeRoundedIcon sx={{ color: "#a5b4fc", mb: 1 }} />
                    <Typography fontWeight={700}>What do you want to understand?</Typography>
                    <Typography variant="body2" sx={{ color: "rgba(255,255,255,0.62)", mt: 0.5 }}>
                      Ask about a tissue, a structure, or a slide. The answer stays in your history.
                    </Typography>
                  </Stack>
                ) : messages.map((msg) => (
                  <Box
                    key={msg.id}
                    sx={{
                      display: "flex",
                      justifyContent: msg.sender === "user" ? "flex-end" : "flex-start",
                      mb: 1.4,
                    }}
                  >
                    <Box
                      sx={{
                        maxWidth: "84%",
                        borderRadius: msg.sender === "user" ? "18px 18px 6px 18px" : "18px 18px 18px 6px",
                        px: 1.6,
                        py: 1.2,
                        color: "#fff",
                        background: msg.sender === "user"
                          ? "linear-gradient(135deg, #4f46e5, #2563eb)"
                          : "rgba(255,255,255,0.07)",
                        border: msg.sender === "user" ? "none" : "1px solid rgba(255,255,255,0.08)",
                      }}
                    >
                      {msg.image ? (
                        <Box
                          component="img"
                          src={msg.image}
                          alt=""
                          sx={{
                            width: "100%",
                            maxWidth: 280,
                            borderRadius: 2,
                            mb: msg.text ? 1 : 0,
                            objectFit: "cover",
                          }}
                        />
                      ) : null}
                      {msg.sender === "ai" ? (
                        renderAiHtml(msg.text || "")
                      ) : (
                        <Typography variant="body2" sx={{ whiteSpace: "pre-wrap", color: "#fff" }}>
                          {msg.text}
                        </Typography>
                      )}
                    </Box>
                  </Box>
                ))}
              </Box>

              <Stack
                direction="row"
                spacing={1}
                alignItems="flex-end"
                sx={{
                  mt: 1.4,
                  p: 0.7,
                  pl: 1.6,
                  borderRadius: 999,
                  backgroundColor: "rgba(255,255,255,0.06)",
                  border: "1px solid rgba(255,255,255,0.1)",
                }}
              >
                <TextField
                  fullWidth
                  value={input}
                  onChange={(e) => setInput(e.target.value)}
                  placeholder={freshQuestion ? "Type your question..." : "Type a follow-up..."}
                  multiline
                  minRows={1}
                  maxRows={4}
                  variant="standard"
                  onKeyDown={(e) => {
                    if (e.key === "Enter" && !e.shiftKey) {
                      e.preventDefault();
                      sendMessage();
                    }
                  }}
                  InputProps={{ disableUnderline: true }}
                  sx={{
                    "& .MuiInputBase-input": { color: "#fff", py: 1 },
                    "& .MuiInputBase-input::placeholder": { color: "rgba(255,255,255,0.45)", opacity: 1 },
                  }}
                />
                <IconButton
                  aria-label="Send"
                  disabled={sending || !input.trim()}
                  onClick={sendMessage}
                  sx={{
                    width: 44,
                    height: 44,
                    color: "#fff",
                    background: "linear-gradient(135deg, #6366f1, #2563eb)",
                    "&.Mui-disabled": { color: "rgba(255,255,255,0.4)", background: "rgba(255,255,255,0.08)" },
                  }}
                >
                  {sending ? <CircularProgress size={18} sx={{ color: "#fff" }} /> : <SendRoundedIcon fontSize="small" />}
                </IconButton>
              </Stack>
            </>
          )}
        </DialogContent>
      </Dialog>
      <InsufficientCreditsModal
        open={!!insufficient}
        required={insufficient?.required}
        remaining={insufficient?.remaining}
        onClose={() => setInsufficient(null)}
      />
    </DashboardLayout>
  );
}

export default AIHistory;
